import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChange,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ActiveElement, ChartData, ChartEvent, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { isDefined, isEqual } from '@fi-shared/utils';
import { IReportChartData } from './fi-report-pie-chart';

@Component({
    selector: 'fi-report-pie-chart',
    templateUrl: './fi-report-pie-chart.component.html',
    styleUrls: ['./fi-report-pie-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-report-pie-chart' },
    standalone: false,
})
export class FiReportPieChartComponent implements OnInit, OnChanges {
    @Input({ required: true }) reportChartData: IReportChartData[];
    @Input() isChartInteractive: boolean;
    @Input() isSingleAlwaysFilled: boolean;
    @Input() isChartSegmentsInteractive: boolean;
    @Input() chartLabel: string;
    @Input() totalItemsCount: number;
    @Input() isSegmentPercentageValueDisplayed: boolean;
    @Input() mainValue: string;
    @Output() onChartClick = new EventEmitter<void>();
    @Output() onChartSegmentClick = new EventEmitter<number>();
    @ViewChild('reportChartElRef', { static: true }) reportChartElRef: ElementRef<HTMLCanvasElement>;
    @ViewChild(BaseChartDirective, { static: true }) chart: BaseChartDirective;

    protected chartData: ChartData;
    protected chartOptions: ChartOptions<'pie'> = {
        animation: {
            duration: 1500,
        },
        events: ['click'],
        responsive: true,
        plugins: {
            tooltip: { enabled: false },
            legend: { display: false },
        },
    };
    private initialSegmentOuterRadius: number;
    private activeSegmentIndex: number;

    constructor(private cdr: ChangeDetectorRef) {}

    public ngOnInit(): void {
        this.buildCustomChart();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        let change: SimpleChange = changes.reportChartData;
        if (change && !isEqual(change.currentValue, change.previousValue)) {
            this.setNewData();
            this.chart.update();
        }
    }

    protected getValue(): number | string {
        if (this.mainValue) {
            return this.mainValue;
        }
        if (this.isChartSegmentsInteractive && isDefined(this.activeSegmentIndex)) {
            const segmentValue: number = this.reportChartData[this.activeSegmentIndex].value;
            if (this.isSegmentPercentageValueDisplayed) {
                return `${Math.round((segmentValue * 100) / this.totalItemsCount)}%`;
            }
            return segmentValue;
        }

        return this.reportChartData.reduce((partialSum: number, item: IReportChartData) => {
            return partialSum + item.value;
        }, 0);
    }

    private setNewData(): void {
        if (!this.chartData) {
            return;
        }

        this.chartData.datasets[0].data = this.getChartDatasetData();
    }

    private buildCustomChart(): void {
        this.chartData = {
            datasets: [
                {
                    data: this.getChartDatasetData(),
                    borderWidth: this.reportChartData.map(() => 0),
                    backgroundColor: this.reportChartData.map((data: IReportChartData) => this.getGradient(data)),
                },
            ],
        };
        if (this.isChartSegmentsInteractive) {
            this.chartOptions.onClick = ($event: ChartEvent, item: ActiveElement[]) => {
                if (!item?.length) {
                    return;
                }

                const slice: ActiveElement = item[0];
                let segments = this.chart.chart.getDatasetMeta(0).data;
                if (!isDefined(this.initialSegmentOuterRadius)) {
                    this.initialSegmentOuterRadius = segments[0]['outerRadius'];
                }

                segments = segments.map((segment, index) => {
                    segment['outerRadius'] =
                        index === slice.index ? this.initialSegmentOuterRadius : this.initialSegmentOuterRadius - 5;
                    return segment;
                });

                this.updateActiveSegmentIndex(item[0].index);
            };
        }
    }

    private getChartDatasetData(): number[] {
        // note: special case when we need show chart with only one item and always filled
        return this.isSingleAlwaysFilled ? [1] : this.reportChartData.map((data: IReportChartData) => data.value);
    }

    private getGradient(data: IReportChartData): CanvasGradient {
        const ctx: CanvasRenderingContext2D = this.reportChartElRef.nativeElement.getContext('2d');
        const gradient: CanvasGradient = ctx.createLinearGradient(0, 0, 0, 50);
        gradient.addColorStop(0, data.gradientColorLight);
        gradient.addColorStop(1, data.gradientColorBright);
        return gradient;
    }

    protected onChartLabelClick(): void {
        if (this.isChartInteractive) {
            this.onChartClick.emit();
        }
        if (this.isChartSegmentsInteractive) {
            this.updateActiveSegmentIndex(undefined);
            this.chart.chart.update();
        }
        this.cdr.markForCheck();
    }

    private updateActiveSegmentIndex(newIndex: number): void {
        this.activeSegmentIndex = newIndex;
        this.onChartSegmentClick.emit(this.activeSegmentIndex);
    }
}
